import React, { useState, useEffect } from "react";
import axios from "axios";
import { X } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ClientFormModal({
  onClose,
  onSuccess,
  API,
  clientToEdit,
}) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    document_type: "cpf",
    document: "",
    cep: "",
    address: "",
    neighborhood: "",
    number: "",
    complement: "",
    phone: "",
    whatsapp: "",
    email: "",
  });

  useEffect(() => {
    if (clientToEdit) {
      setFormData({
        name: clientToEdit.name || "",
        document_type: clientToEdit.document_type || "cpf",
        document: clientToEdit.document || "",
        cep: clientToEdit.cep || "",
        address: clientToEdit.address || "",
        neighborhood: clientToEdit.neighborhood || "",
        number: clientToEdit.number || "",
        complement: clientToEdit.complement || "",
        phone: clientToEdit.phone || "",
        whatsapp: clientToEdit.whatsapp || "",
        email: clientToEdit.email || "",
      });
    }
  }, [clientToEdit]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCepBlur = async () => {
    const cleanCep = formData.cep.replace(/\D/g, "");
    if (cleanCep.length === 8) {
      try {
        const res = await axios.get(
          `https://viacep.com.br/ws/${cleanCep}/json/`,
          {
            withCredentials: false,
          },
        );

        if (!res.data.erro) {
          setFormData((prev) => ({
            ...prev,
            address: res.data.logradouro,
            neighborhood: res.data.bairro,
          }));
          toast.success("Endereço encontrado!");
        } else {
          toast.error("CEP não encontrado.");
        }
      } catch (err) {
        toast.error("Erro ao buscar CEP.");
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...formData };
      if (payload.email === "") payload.email = null;
      if (payload.cep === "") payload.cep = null;

      const isEditing = !!clientToEdit;
      const url = isEditing
        ? `${API}/clients/${clientToEdit.id}`
        : `${API}/clients`;
      const method = isEditing ? "put" : "post";

      const res = await axios[method](url, payload, {
        withCredentials: true,
      });

      toast.success(
        isEditing
          ? "Cliente atualizado com sucesso!"
          : "Cliente cadastrado com sucesso!",
      );
      onSuccess(res.data);
      onClose();
    } catch (err) {
      if (err.response?.status === 400) {
        toast.error(err.response.data.detail);
      } else {
        toast.error(
          clientToEdit
            ? "Erro ao atualizar cliente."
            : "Erro ao cadastrar cliente.",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/95 z-[60] flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-[#121212] rounded-xl p-5 sm:p-6 w-full max-w-2xl shadow-2xl border border-white/10 my-auto will-change-transform">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-bold text-lg">
            {clientToEdit ? "Editar Cliente" : "Cadastrar Novo Cliente"}
          </h3>
          <button
            onClick={onClose}
            className="hover:bg-[#1A1A1A] p-2 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="text-xs text-[#A1A1AA] block mb-1">
                Nome Completo / Razão Social *
              </label>
              <input
                required
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="input-field w-full h-11"
              />
            </div>

            <div>
              <label className="text-xs text-[#A1A1AA] block mb-1">
                Tipo de Pessoa *
              </label>
              <div className="flex gap-4 h-11 items-center">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="document_type"
                    value="cpf"
                    checked={formData.document_type === "cpf"}
                    onChange={handleChange}
                    className="accent-[#00E676]"
                  />{" "}
                  CPF
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="document_type"
                    value="cnpj"
                    checked={formData.document_type === "cnpj"}
                    onChange={handleChange}
                    className="accent-[#00E676]"
                  />{" "}
                  CNPJ
                </label>
              </div>
            </div>

            <div>
              <label className="text-xs text-[#A1A1AA] block mb-1">
                {formData.document_type.toUpperCase()} *
              </label>
              <input
                required
                name="document"
                value={formData.document}
                onChange={handleChange}
                className="input-field w-full h-11"
              />
            </div>

            <div>
              <label className="text-xs text-[#A1A1AA] block mb-1">
                CEP (Apenas números)
              </label>
              <input
                name="cep"
                value={formData.cep}
                onChange={handleChange}
                onBlur={handleCepBlur}
                maxLength="8"
                className="input-field w-full h-11"
                placeholder="Digite para buscar..."
              />
            </div>

            <div className="sm:col-span-2">
              <label className="text-xs text-[#A1A1AA] block mb-1">
                Endereço
              </label>
              <input
                name="address"
                value={formData.address}
                onChange={handleChange}
                className="input-field w-full h-11"
              />
            </div>

            <div>
              <label className="text-xs text-[#A1A1AA] block mb-1">
                Número
              </label>
              <input
                name="number"
                value={formData.number}
                onChange={handleChange}
                className="input-field w-full h-11"
              />
            </div>

            <div>
              <label className="text-xs text-[#A1A1AA] block mb-1">
                Complemento
              </label>
              <input
                name="complement"
                value={formData.complement}
                onChange={handleChange}
                className="input-field w-full h-11"
              />
            </div>

            <div className="sm:col-span-2">
              <label className="text-xs text-[#A1A1AA] block mb-1">
                Bairro
              </label>
              <input
                name="neighborhood"
                value={formData.neighborhood}
                onChange={handleChange}
                className="input-field w-full h-11"
              />
            </div>

            <div>
              <label className="text-xs text-[#A1A1AA] block mb-1">
                Telefone
              </label>
              <input
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="input-field w-full h-11"
              />
            </div>

            <div>
              <label className="text-xs text-[#A1A1AA] block mb-1">
                WhatsApp
              </label>
              <input
                name="whatsapp"
                value={formData.whatsapp}
                onChange={handleChange}
                className="input-field w-full h-11"
              />
            </div>

            <div className="sm:col-span-2">
              <label className="text-xs text-[#A1A1AA] block mb-1">
                E-mail
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="input-field w-full h-11"
              />
            </div>
          </div>

          <div className="flex gap-2 pt-4 border-t border-white/10">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary flex-1 h-12 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary flex-1 h-12 transition-colors disabled:opacity-50"
            >
              {loading
                ? "Salvando..."
                : clientToEdit
                  ? "Salvar Alterações"
                  : "Salvar Cliente"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
